import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PatchProjectData, GetCurrentProjectData } from '../../redux/projectDataSlice';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { normalizeProjectEnabledFeatures } from '../../utils/lensFeatures';
import styles from './css/index.module.css';

// Features that cannot be disabled (always enabled)
const REQUIRED_FEATURES = ['project_overview', 'dataroom'];

// Feature categories matching the backend structure
const FEATURE_CATEGORIES = {
    strategic_assessment: {
        name: 'Strategic Assessment & Monitoring',
        features: {
            project_overview: 'Project Overview',
            maturity_index: 'Maturity Index',
            security_index: 'Security Index',
            team_analyser: 'Team Analyser',
            security_governance: 'Security Governance',
        },
    },
    due_diligence: {
        name: 'Due Diligence & Knowledge',
        features: {
            dataroom: 'Dataroom',
            dataroom_wizard: 'Dataroom Wizard',
            information_requests: 'Information Requests',
            observations: 'Observations',
            lessons_learnt: 'Lessons Learnt',
            journal: 'Journal',
            the_tent: 'The Tent',
            lens_intelligence: 'Lens Intelligence',
        },
    },
    project_risk_management: {
        name: 'Project & Risk Management',
        features: {
            project_management: 'Project Management',
            raid_log: 'RAID Log',
            action_log: 'Action Log',
        },
    },
    technical_service: {
        name: 'Technical & Service Delivery',
        features: {
            software_inventory: 'Software Inventory',
            hardware_inventory: 'Hardware Inventory',
            ticket_management: 'Ticket Management',
        },
    },
};

const FeatureVisibility = ({ compareObjectsValues }) => {
    const dispatch = useDispatch();
    const { selectedProject, projectDetailButtonLoading } = useSelector(state => state.projectData);
    const { user, memberships, activeWorkspaceId } = useSelector(state => state.user);

    // Find active workspace membership for defaults
    const activeMembership = React.useMemo(() => {
        if (!Array.isArray(memberships) || memberships.length === 0) return null;
        // Prefer match on activeWorkspaceId, fall back to first membership.
        const byActiveId = memberships.find(m => m?.workspace?.id === activeWorkspaceId);
        return byActiveId || memberships[0];
    }, [memberships, activeWorkspaceId]);

    const workspaceDefaultFeatures = React.useMemo(() => {
        return (
            (activeMembership &&
                Array.isArray(activeMembership.workspace?.default_lens_features) &&
                activeMembership.workspace.default_lens_features) ||
            []
        );
    }, [activeMembership]);

    // Check if user is project admin
    const isProjectAdmin = React.useMemo(() => {
        if (!selectedProject || !user) return false;
        return selectedProject?.vdr?.vdr_admin === user?.id || selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);
    }, [selectedProject, user]);

    // Initialize enabled_features state
    const [enabledFeatures, setEnabledFeatures] = React.useState({});

    const allFeatureKeys = React.useMemo(
        () => Object.values(FEATURE_CATEGORIES).flatMap(category => Object.keys(category.features)),
        [],
    );

    const handleSetFormValues = () => {
        if (selectedProject?.enabled_features) {
            const source = selectedProject.enabled_features || {};
            const features = normalizeProjectEnabledFeatures(source, allFeatureKeys);
            // Ensure required features are always enabled
            REQUIRED_FEATURES.forEach(key => {
                features[key] = true;
            });
            setEnabledFeatures(features);
        } else {
            // If no enabled_features set, initialize using workspace defaults (fall back to all enabled).
            const defaultKeys =
                workspaceDefaultFeatures && workspaceDefaultFeatures.length
                    ? workspaceDefaultFeatures
                    : Object.values(FEATURE_CATEGORIES).flatMap(category => Object.keys(category.features));

            const allFeatures = {};
            Object.values(FEATURE_CATEGORIES).forEach(category => {
                Object.keys(category.features).forEach(featureKey => {
                    allFeatures[featureKey] = defaultKeys.includes(featureKey);
                });
            });
            // Ensure required features are always enabled
            REQUIRED_FEATURES.forEach(key => {
                allFeatures[key] = true;
            });
            setEnabledFeatures(allFeatures);
        }
    };

    React.useEffect(() => {
        handleSetFormValues();
    }, [selectedProject]);

    const handleToggleFeature = featureKey => {
        // Prevent disabling required features
        if (REQUIRED_FEATURES.includes(featureKey)) {
            return;
        }
        setEnabledFeatures(prev => ({
            ...prev,
            [featureKey]: !prev[featureKey],
        }));
    };

    const handleSave = async () => {
        if (!selectedProject?.id) return;

        // Ensure required features are always enabled before saving
        const featuresToSave = { ...enabledFeatures };
        REQUIRED_FEATURES.forEach(key => {
            featuresToSave[key] = true;
        });

        await dispatch(
            PatchProjectData({
                id: selectedProject.id,
                data: { enabled_features: featuresToSave },
            }),
        );
    };

    // Create a comparison object for compareObjectsValues
    const getComparisonObject = () => {
        return { enabled_features: enabledFeatures };
    };

    // Don't render if user is not admin
    if (!isProjectAdmin) {
        return null;
    }

    return (
        <div className={styles.componentCard}>
            <h5 className={styles.componentTitle + ' mb-0'}>Lens Features</h5>
            <p className={styles.formSubtitle + ' mb-5'}>Select Lens Features for the Project</p>

            <div className={styles.featureCategories}>
                {Object.entries(FEATURE_CATEGORIES).map(([categoryKey, category]) => (
                    <div key={categoryKey} className={styles.featureCategory}>
                        <h4 className={styles.categoryTitle}>{category.name}</h4>
                        <div className={styles.featureList}>
                            {Object.entries(category.features).map(([featureKey, featureName]) => {
                                const isRequired = REQUIRED_FEATURES.includes(featureKey);
                                const isChecked = enabledFeatures[featureKey] !== false;
                                return (
                                    <div key={featureKey} className={styles.featureItem}>
                                        <label className={styles.featureCheckbox} style={isRequired ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                                            <input type="checkbox" checked={isChecked} disabled={isRequired} onChange={() => handleToggleFeature(featureKey)} />
                                            <span>
                                                {featureName}
                                                {isRequired && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>(Required)</span>}
                                            </span>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.buttonDiv}>
                {!compareObjectsValues(selectedProject, getComparisonObject()) && (
                    <div className="d-flex align-items-center justify-content-between">
                        <button onClick={handleSave} className={styles.submitButton}>
                            Save {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                        </button>
                        <span onClick={handleSetFormValues} className={styles.linkBtn}>
                            Revert
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FeatureVisibility;
